import { useState, useCallback } from 'react'

// Matches the return type from useChatHistory.startChatTitleGeneration
interface TitleGenerationResult {
  started: boolean
}

type UseChatTitleGenerationArgs = {
  startTitleGen: (chatId: number, message: string) => Promise<TitleGenerationResult>
}

export function useChatTitleGeneration({
  startTitleGen,
}: UseChatTitleGenerationArgs) {
  const [pendingTitleChatIds, setPendingTitleChatIds] = useState<Set<number>>(new Set())

  const addPendingTitleChatId = useCallback((chatId: number) => {
    setPendingTitleChatIds(prev => {
      if (prev.has(chatId)) {
        return prev
      }
      const next = new Set(prev)
      next.add(chatId)
      return next
    })
  }, [])

  const removePendingTitleChatId = useCallback((chatId: number) => {
    setPendingTitleChatIds(prev => {
      if (!prev.has(chatId)) {
        return prev
      }
      const next = new Set(prev)
      next.delete(chatId)
      return next
    })
  }, [])

  const startChatTitleGeneration = useCallback((chatIdToUpdate: number, message: string) => {
    addPendingTitleChatId(chatIdToUpdate)
    ;(async() => {
      try {
        await startTitleGen(chatIdToUpdate, message)
      } finally {
        removePendingTitleChatId(chatIdToUpdate)
      }
    })()
  }, [addPendingTitleChatId, removePendingTitleChatId, startTitleGen])

  return {
    pendingTitleChatIds,
    addPendingTitleChatId,
    removePendingTitleChatId,
    startChatTitleGeneration,
  }
}
